// VendorNeedsBoard.jsx - Vendor punch list board with print functionality
import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import VinDisplay from '../shared/VinDisplay';
import { VENDOR_TYPES } from './constants';

// Vendor type labels mapping
const VENDOR_LABELS = {
  pdr: 'PDR',
  body_outside: 'Body - Outside',
  body_inhouse: 'Body - In House',
  rim_wheel: 'Rim/Wheel',
  interior: 'Interior',
  glass: 'Glass',
  detail: 'Detail',
  mechanical_outside: 'Mechanical - Outside',
  other: 'Other'
};

// Main Vendor Needs Board Component
const VendorNeedsBoard = ({ vendorNeeds, onVehicleClick }) => {
  const [selectedVendorType, setSelectedVendorType] = useState(null);

  if (!vendorNeeds || vendorNeeds.length === 0) {
    return null;
  }

  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-4">
      <h3 className="font-semibold mb-3 flex items-center gap-2">
        <span className="text-lg">🔧</span>
        Outside Vendors Board
      </h3>
      <p className="text-xs text-neutral-500 mb-3">Click a vendor type to see vehicles and print a punch list</p>
      
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {vendorNeeds.map(vn => {
          const label = VENDOR_LABELS[vn.vendor_type] || vn.vendor_type;
          
          return (
            <button
              key={vn.vendor_type}
              onClick={() => setSelectedVendorType(vn.vendor_type)}
              className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-left hover:bg-purple-100 transition"
            >
              <div className="flex items-center justify-between mb-1">
                <span className="font-medium text-purple-800 text-sm truncate pr-2">
                  {label}
                </span>
                <span className="bg-purple-600 text-white text-xs px-2 py-0.5 rounded-full flex-shrink-0">
                  {vn.count}
                </span>
              </div>
              <div className="text-xs text-purple-600">
                {vn.count} vehicle{vn.count !== 1 ? 's' : ''} need this
              </div>
            </button>
          );
        })}
      </div>

      {/* Vendor List Modal */}
      {selectedVendorType && (
        <VendorListModal
          vendorType={selectedVendorType}
          onClose={() => setSelectedVendorType(null)}
          onVehicleClick={onVehicleClick}
        />
      )}
    </div>
  );
};

// Vendor List Modal with Print Functionality
const VendorListModal = ({ vendorType, onClose, onVehicleClick }) => {
  const [punchList, setPunchList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const typeLabel = VENDOR_LABELS[vendorType] || vendorType;

  useEffect(() => {
    const loadPunchList = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await api.getVendorPunchList(vendorType);
        setPunchList(data || []);
      } catch (err) {
        console.error('Failed to load punch list:', err);
        setError('Failed to load punch list');
      } finally {
        setLoading(false);
      }
    };
    loadPunchList();
  }, [vendorType]);

  const handlePrint = () => {
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${typeLabel} Punch List</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { 
              font-family: Arial, sans-serif; 
              padding: 20px;
              font-size: 12px;
            }
            h1 { 
              font-size: 18px; 
              margin-bottom: 5px;
              border-bottom: 2px solid #333;
              padding-bottom: 5px;
            }
            .date { 
              font-size: 10px; 
              color: #666; 
              margin-bottom: 15px;
            }
            table { 
              width: 100%; 
              border-collapse: collapse;
              margin-top: 10px;
            }
            th, td { 
              border: 1px solid #ccc; 
              padding: 8px; 
              text-align: left; 
              font-size: 11px;
            }
            th { 
              background: #f5f5f5;
              font-weight: bold;
            }
            tr:nth-child(even) {
              background: #fafafa;
            }
            .notes-cell {
              max-width: 200px;
              word-wrap: break-word;
            }
            @media print {
              body { padding: 10px; }
              table { page-break-inside: auto; }
              tr { page-break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <h1> ${typeLabel} Punch List</h1>
          <p class="date">Generated: ${new Date().toLocaleString()}</p>
          <p style="margin-bottom: 10px;"><strong>${punchList.length}</strong> vehicle${punchList.length !== 1 ? 's' : ''} need this service</p>
          
          <table>
            <thead>
              <tr>
                <th style="width: 80px;">Stock #</th>
                <th style="width: 130px;">Vehicle</th>
                <th style="width: 60px;">Color</th>
                <th style="width: 140px;">VIN</th>
                <th class="notes-cell">Notes / Work Needed</th>
              </tr>
            </thead>
            <tbody>
              ${punchList.map(v => `
                <tr>
                  <td><strong>${v.stockNumber || '-'}</strong></td>
                  <td>${v.year || ''} ${v.make || ''} ${v.model || ''}</td>
                  <td>${v.color || '-'}</td>
                  <td style="font-family: monospace; font-size: 10px;">${v.vin || '-'}</td>
                  <td class="notes-cell">${v.vendorNotes || v.complaint || '-'}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          
          <div style="margin-top: 30px; border-top: 1px solid #ccc; padding-top: 10px;">
            <p style="font-size: 10px; color: #666;">
              □ All items completed &nbsp;&nbsp;&nbsp; Signature: __________________ &nbsp;&nbsp;&nbsp; Date: __________
            </p>
          </div>
        </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      // Wait for content to load before printing
      printWindow.onload = () => {
        printWindow.print();
      };
    }
  };

  const handleVehicleClick = (vehicle) => {
    if (onVehicleClick) {
      onClose();
      onVehicleClick(vehicle);
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" 
      onClick={onClose}
    >
      <div 
        className="bg-white w-full max-w-3xl rounded-xl shadow-xl max-h-[85vh] overflow-hidden flex flex-col" 
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 border-b flex justify-between items-center flex-shrink-0">
          <div>
            <h2 className="font-semibold text-lg flex items-center gap-2">
               {typeLabel}
            </h2>
            <p className="text-sm text-neutral-500">
              {loading ? 'Loading...' : `${punchList.length} vehicle${punchList.length !== 1 ? 's' : ''} need this service`}
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handlePrint}
              disabled={loading || punchList.length === 0}
              className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
            >
              🖨️ Print Punch List
            </button>
            <button
              onClick={onClose}
              className="text-neutral-400 hover:text-neutral-600 text-2xl px-2 leading-none"
            >
              ×
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto flex-1">
          {loading ? (
            <div className="text-center text-neutral-400 py-12">
              <div className="text-3xl mb-2"></div>
              <div>Loading punch list...</div>
            </div>
          ) : error ? (
            <div className="text-center text-red-500 py-12">
              <div className="text-3xl mb-2"></div>
              <div>{error}</div>
            </div>
          ) : punchList.length === 0 ? (
            <div className="text-center text-neutral-400 py-12">
              <div className="text-3xl mb-2">✓</div>
              <div>No vehicles currently need this service</div>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-neutral-500 border-b">
                  <th className="pb-2 pr-2">Stock #</th>
                  <th className="pb-2 pr-2">Vehicle</th>
                  <th className="pb-2 pr-2">Color</th>
                  <th className="pb-2 pr-2">VIN</th>
                  <th className="pb-2">Notes</th>
                </tr>
              </thead>
              <tbody>
                {punchList.map((v, index) => (
                  <tr 
                    key={index} 
                    className="border-b border-neutral-100 hover:bg-neutral-50 cursor-pointer"
                    onClick={() => handleVehicleClick(v)}
                  >
                    <td className="py-3 pr-2">
                      <span className="font-mono font-medium">{v.stockNumber || '-'}</span>
                    </td>
                    <td className="py-3 pr-2">
                      <div className="font-medium">{v.year} {v.make} {v.model}</div>
                    </td>
                    <td className="py-3 pr-2 text-xs">
                      {v.color || '-'}
                    </td>
                    <td className="py-3 pr-2">
                      <VinDisplay vin={v.vin} />
                    </td>
                    <td className="py-3 text-xs text-neutral-600 max-w-xs">
                      {v.vendorNotes || v.complaint || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        {!loading && punchList.length > 0 && (
          <div className="p-3 border-t bg-neutral-50 flex-shrink-0">
            <div className="text-xs text-neutral-500 text-center">
              Click on a vehicle to view its Repair Order • Click "Print Punch List" to generate a printable version
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VendorNeedsBoard;
